import React, { forwardRef, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Chip, Paper, Stack, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import ChecklistIcon from '@mui/icons-material/Checklist'
import moment from 'moment'
import { SEVERITY } from '../makeReady/severity'
import { UserContext } from '../../../App'
import { canEditLoadLists } from '../makeReadyModule/makeReadyAccess'
import BoardLocation, { groupMeta } from './BoardLocation'

// Status-only card for the board: level, counts and actions. The individual
// items live on the check screen (Run check), not here.
const TrackedItemCard = forwardRef(
  ({ item, onOpen }, ref) => {
    const navigate = useNavigate()
    const { user } = useContext(UserContext)
    const canEditLoadList = canEditLoadLists(user)
    const severity = SEVERITY[item.level]
    const { Icon } = groupMeta(item.group)
    const offListCount = item.offListCount || 0

    return (
      <Paper
        ref={ref}
        elevation={1}
        sx={{
          borderLeft: '6px solid',
          borderLeftColor: severity.hex,
          overflow: 'hidden',
          transition: 'box-shadow .15s ease',
          '&:hover': { boxShadow: 4 },
        }}
      >
        <Stack
          sx={{ p: 1.5, cursor: onOpen ? 'pointer' : 'default' }}
          gap={1}
          onClick={onOpen}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={1}
          >
            <Stack direction="row" gap={1} alignItems="center">
              <Icon sx={{ color: severity.hex }} />
              <Stack>
                <Typography fontWeight={700}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.subLabel}
                </Typography>
              </Stack>
            </Stack>
            <Chip
              size="small"
              label={severity.label}
              sx={{ bgcolor: severity.hex, color: '#fff', fontWeight: 600 }}
            />
          </Stack>

          {(item.missingCount > 0 ||
            item.expiredCount > 0 ||
            item.expiringSoonCount > 0 ||
            offListCount > 0) && (
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {item.expiredCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="error"
                  label={`${item.expiredCount} expired`}
                />
              )}
              {item.missingCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="secondary"
                  label={`${item.missingCount} missing`}
                />
              )}
              {item.expiringSoonCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={`${item.expiringSoonCount} expiring soon`}
                />
              )}
              {offListCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${offListCount} not on load list`}
                />
              )}
            </Stack>
          )}

          <BoardLocation location={item.location} />

          <Typography variant="caption" color="text.secondary">
            {item.lastCheckedAt
              ? `Checked ${moment(item.lastCheckedAt).fromNow()}`
              : 'Never checked'}
            {item.nextExpiryAt && (
              <>
                {' · '}
                <span
                  title={moment(item.nextExpiryAt).format('DD/MM/YYYY HH:mm')}
                >
                  Next expiry {moment(item.nextExpiryAt).format('DD/MM/YYYY')}
                </span>
              </>
            )}
          </Typography>

          <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              variant={item.level === 'good' ? 'outlined' : 'contained'}
              startIcon={<ChecklistIcon />}
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/make-ready/${item.id}`)
              }}
            >
              {item.level === 'neverChecked' ? 'Run first check' : 'Run check'}
            </Button>
            {canEditLoadList && item.loadListId && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/make-ready-load-lists/${item.loadListId}`)
                }}
              >
                Edit load list
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    )
  },
)

TrackedItemCard.displayName = 'TrackedItemCard'

export default TrackedItemCard
